import '../../css/main.css';
import '../../css/pages/admin.css';
import '../../css/components/buttons.css';
import '../../css/components/forms.css';
import '../../css/components/modals.css';
import '../../css/components/loader.css';
import '../../css/utility.css';

import { ready } from '../utils.js';
import { apiFetch } from '../api.js';
import { dom, cacheDomElements } from '../dom.js';
import { updateUIForLoggedInState, updateUIForLoggedOutState, showError, hideError } from '../ui.js';

let allUsers = [];
let allInvitations = [];

async function initializeAdminDashboard() {
    cacheDomElements();
    setupEventListeners();
    checkSessionAndLoadAdminData();
}

async function checkSessionAndLoadAdminData() {
    try {
        const user = await apiFetch("/api/v1/session", { cache: "no-store" });
        if (user && user.is_admin) {
            updateUIForLoggedInState(user);
            loadAdminData();
        } else {
            // Not an admin or not logged in, redirect to unauthorized
            window.location.href = "/unauthorized.html";
        }
    } catch (error) {
        console.error("Session check failed:", error);
        updateUIForLoggedOutState();
        window.location.href = "/unauthorized.html";
    }
}

function setupEventListeners() {
    if (dom.inviteForm) {
        dom.inviteForm.addEventListener('submit', handleInviteSubmit);
    }
    if (dom.errorButton) {
        dom.errorButton.addEventListener('click', hideError);
    }
    if (dom.deleteConfirmButton) {
        dom.deleteConfirmButton.addEventListener('click', executeDeleteAdminItem);
    }
    if (dom.deleteCancelButton) {
        dom.deleteCancelButton.addEventListener('click', hideDeleteConfirm);
    }
}

async function loadAdminData() {
    try {
        const data = await apiFetch('/api/v1/admin/users');
        allUsers = data.users.filter(u => !u.invited);
        allInvitations = data.users.filter(u => u.invited);
        renderUsers();
        renderInvitations();
    } catch (error) {
        showError(error);
        console.error("Failed to load admin data:", error);
    }
}

function renderUsers() {
    if (!dom.usersList) return;
    dom.usersList.innerHTML = '';

    if (allUsers.length === 0) {
        dom.usersList.innerHTML = '<li class="empty-list-item">No active users.</li>';
        return;
    }

    const fragment = document.createDocumentFragment();
    allUsers.forEach(user => {
        const li = document.createElement('li');
        li.className = 'admin-list-item';
        li.innerHTML = `
            <span>${user.email} ${user.is_admin ? '(Admin)' : ''}</span>
            <button class="delete-user-btn button-danger" data-id="${user.id}" data-name="${user.email}" title="Delete User">
                <span class="material-symbols-outlined">delete</span>
            </button>
        `;
        li.querySelector('.delete-user-btn').addEventListener('click', promptForDeleteAdminItem);
        fragment.appendChild(li);
    });
    dom.usersList.appendChild(fragment);
}

function renderInvitations() {
    if (!dom.invitationsList) return;
    dom.invitationsList.innerHTML = '';

    if (allInvitations.length === 0) {
        dom.invitationsList.innerHTML = '<li class="empty-list-item">No pending invitations.</li>';
        return;
    }

    const fragment = document.createDocumentFragment();
    allInvitations.forEach(invitation => {
        const li = document.createElement('li');
        li.className = 'admin-list-item';
        li.innerHTML = `
            <span>${invitation.email} (Pending)</span>
            <button class="revoke-invite-btn button-danger" data-email="${invitation.email}" data-name="${invitation.email}" title="Revoke Invitation">
                <span class="material-symbols-outlined">cancel</span>
            </button>
        `;
        li.querySelector('.revoke-invite-btn').addEventListener('click', promptForDeleteAdminItem);
        fragment.appendChild(li);
    });
    dom.invitationsList.appendChild(fragment);
}

async function handleInviteSubmit(e) {
    e.preventDefault();
    const emailInput = dom.inviteForm.querySelector('#invite-email');
    const email = emailInput.value;

    try {
        await apiFetch('/api/v1/admin/invite', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email })
        });
        emailInput.value = '';
        loadAdminData(); // Refresh data
    } catch (error) {
        showError(error);
        console.error("Failed to send invitation:", error);
    }
}

function promptForDeleteAdminItem(e) {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    let type, id, email, name;

    if (btn.classList.contains('delete-user-btn')) {
        type = 'user';
        id = btn.dataset.id;
        name = btn.dataset.name;
    } else if (btn.classList.contains('revoke-invite-btn')) {
        type = 'invitation';
        email = btn.dataset.email;
        name = btn.dataset.name;
    } else {
        return; // Should not happen
    }

    dom.deleteConfirmMessage.innerHTML = `Are you sure you want to delete this ${type}:<br /> <i>${name}</i>? <br /><br />This action cannot be undone.`;
    dom.deleteConfirmButton.dataset.deleteType = type;
    dom.deleteConfirmButton.dataset.deleteId = id || ''; // User ID or empty
    dom.deleteConfirmButton.dataset.deleteEmail = email || ''; // Invitation email or empty
    dom.deleteConfirmModal.classList.add("show");
}

async function executeDeleteAdminItem(e) {
    const btn = e.currentTarget;
    const type = btn.dataset.deleteType;
    const id = btn.dataset.deleteId;
    const email = btn.dataset.deleteEmail;

    let url;
    if (type === 'user') {
        url = `/api/v1/admin/user/${id}`;
    } else if (type === 'invitation') {
        url = `/api/v1/admin/invite/${email}`;
    } else {
        hideDeleteConfirm();
        return;
    }

    try {
        await apiFetch(url, { method: 'DELETE' });
        hideDeleteConfirm();
        loadAdminData(); // Refresh data
    } catch (error) {
        showError(error);
        console.error(`Failed to delete ${type}:`, error);
        hideDeleteConfirm();
    }
}

function hideDeleteConfirm() {
    dom.deleteConfirmModal.classList.remove("show");
}

ready(initializeAdminDashboard);
